import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wallet, CheckCircle2, Clock, Check, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { money } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

function Fin({ icon: Icon, label, value, accent, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent}`}><Icon size={18} strokeWidth={1.5} /></span>
      </div>
      <div className="mt-3 font-display text-2xl lg:text-3xl font-bold text-slate-900">{value}</div>
    </motion.div>
  );
}

function OrderList({ title, orders, testid, onRespond, busyId }) {
  const { t } = useI18n();
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid={testid}>
      <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">{title} <span className="text-slate-400 text-sm">({orders.length})</span></h3>
      <div className="space-y-2">
        {orders.length === 0 && <p className="text-sm text-slate-400">{t("common.none")}</p>}
        {orders.map((o) => (
          <Link key={o.id} to={`/orders/${o.id}`} className="block p-2 -mx-2 rounded-lg hover:bg-slate-50" data-testid="partner-order-row">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-slate-800">{o.number}</div>
                <div className="text-xs text-slate-400">{o.origin} → {o.destination}</div>
              </div>
              <div className="flex items-center gap-2">
                {(o.my_amount ?? o.partner_payout?.amount) > 0 && (
                  <span className={`text-xs font-medium ${o.my_paid ? "text-emerald-600" : "text-amber-600"}`}>
                    {money(o.my_amount ?? o.partner_payout?.amount, o.currency)}
                  </span>
                )}
                <StatusBadge status={o.status} />
              </div>
            </div>
            {o.partner_acceptance === "waiting" && onRespond && (
              <div className="flex gap-2 mt-2" data-testid={`partner-respond-${o.id}`}>
                <Button size="sm" disabled={busyId === o.id}
                  onClick={(e) => { stop(e); onRespond(o.id, "accept"); }}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-8" data-testid={`partner-accept-${o.id}`}>
                  <Check size={14} className="mr-1" /> {t("pd.accept")}
                </Button>
                <Button size="sm" variant="outline" disabled={busyId === o.id}
                  onClick={(e) => { stop(e); onRespond(o.id, "refuse"); }}
                  className="flex-1 border-rose-300 text-rose-600 hover:bg-rose-50 h-8" data-testid={`partner-decline-${o.id}`}>
                  <X size={14} className="mr-1" /> {t("pd.decline")}
                </Button>
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function PartnerDashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/dashboard/partner").then(({ data }) => setData(data))
    .catch(() => setData({ orders: { active: [], future: [], completed: [] }, financial: {} }));
  useEffect(() => { load(); }, []);

  const respond = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/orders/${id}/partner-response`, { action });
      toast.success(action === "accept" ? t("pd.accepted") : t("pd.declined"));
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  if (!data) return <div className="text-slate-400 py-10">{t("common.loading")}</div>;
  const f = data.financial || {};

  return (
    <div className="space-y-6" data-testid="partner-dashboard">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t("pd.welcome")}, {user?.name}</h1>
        <p className="text-sm text-slate-500 mt-1 capitalize">{user?.role}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Fin icon={Wallet} label={t("pd.earned")} value={money(f.total_earned || 0, f.currency)} accent="bg-slate-100 text-slate-600" testid="partner-total-earned" />
        <Fin icon={CheckCircle2} label={t("pd.paid")} value={money(f.total_paid || 0, f.currency)} accent="bg-emerald-50 text-emerald-700" testid="partner-total-paid" />
        <Fin icon={Clock} label={t("pd.pending")} value={money(f.total_pending || 0, f.currency)} accent="bg-amber-50 text-amber-700" testid="partner-total-pending" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <OrderList title={t("pd.activeOrders")} orders={data.orders.active} testid="partner-active-orders" onRespond={respond} busyId={busyId} />
        <OrderList title={t("pd.future")} orders={data.orders.future} testid="partner-future-orders" onRespond={respond} busyId={busyId} />
        <OrderList title={t("pd.completed")} orders={data.orders.completed} testid="partner-completed-orders" />
      </div>

      {(data.orders.refused?.length > 0) && (
        <div data-testid="partner-refused-section">
          <OrderList title={t("pd.refused")} orders={data.orders.refused} testid="partner-refused-orders" />
        </div>
      )}
    </div>
  );
}
